import * as React from "react";
import { useSelector } from "react-redux";
import {
  getMaterialDetails,
  getMaterials,
  getMaterialsBulk,
  exportMaterialContractsToCSV,
} from "../../redux/material/actions";
import {
  setParameters,
  removeParameter,
  clearParameters,
  setDateParameters,
} from "../../redux/global/actions";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Button from "@mui/material/Button";
import MultiValueInput from "../../inputs/multiValueInput";
import {
  Box,
  Grid,
  Stack,
  Tabs,
  Tab,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useEffect } from "react";
import MultiValueInputWithWarning from "../../inputs/multiValueInputWithWarning";
import SearchDateInput from "../../inputs/searchDateInput";
import MaterialChildren from "./materialChildren";
import MaterialParents from "./materialParents";
import {
  downloadCSV,
  downloadJSON,
  extractUserInfoFromToken,
  getLocalStorageSAMLToken,
} from "../../utils";
import MultiSelector from "../../inputs/MultiSelector";
import { DataTable, MaterialSymbol } from "@datalake/towercontrol-banner";
import ExportMenu from "../../widgets/ExportMenu";

const MATERIAL_COLUMNS = [
  {
    id: "serial_number",
    label: "Serial Number",
    filter: "text",
    nowrap: true,
  },
  { id: "creation_date", label: "Creation Date", nowrap: true },
  { id: "modification_date", label: "Modification Date", nowrap: true },
  { id: "part_number", label: "Part Number", filter: "text" },
  { id: "sold_to", label: "Sold To", filter: "text" },
  {
    id: "business_partner_company_id",
    label: "Business Partner",
    filter: "text",
  },
  { id: "order_id", label: "Order ID", filter: "text" },
];

export default function Materials(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryParameters = useSelector((state) => state?.parameters?.materials);
  const [currentSerialNumber, setCurrentSerialNumber] = React.useState();
  const [currentBusinessPartner, setCurrentBusinessPartner] = React.useState();
  const [currentSoldTo, setCurrentSoldTo] = React.useState();
  const [currentOrder, setCurrentOrder] = React.useState();
  const [currentEndUser, setCurrentEndUser] = React.useState();
  const [currentBundleReference, setCurrentBundleReference] = React.useState();
  const [currentSupportPartner, setCurrentSupportPartner] = React.useState();
  const [selectedTab, setSelectedTab] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const tabs = [
    {
      label: "Materials",
      filters: [
        { type: "serialNumbers", label: "Serial Number" },
        { type: "businessPartners", label: "Business Partner" },
        { type: "soldTos", label: "Sold To" },
        { type: "order", label: "SAP Order" },
        { type: "endUser", label: "End Customer ID" },
        { type: "supportPartner", label: "Support Partner" },
        { type: "creationDate", label: "Creation Date" },
      ],
    },
    {
      label: "Product",
      filters: [{ type: "bundleReference", label: "Bundle Reference" }],
    },
    {
      label: "Monitoring Material Swap",
      filters: [{ type: "swapStatus", label: "Swap Status" }],
    },
  ];

  const handleExecuteSearch = () => {
    let queryObject = {};
    const currentTab = tabs[selectedTab];

    currentTab.filters.forEach((filter) => {
      switch (filter.type) {
        case "serialNumbers":
          if (queryParameters?.serialNumbers)
            queryObject.serial_number = queryParameters.serialNumbers;
          break;
        case "businessPartners":
          if (queryParameters?.businessPartners)
            queryObject.business_partner = queryParameters.businessPartners;
          break;
        case "soldTos":
          if (queryParameters?.soldTos)
            queryObject.sold_to = queryParameters.soldTos;
          break;
        case "order":
          if (queryParameters?.order) queryObject.order = queryParameters.order;
          break;
        case "endUser":
          if (queryParameters?.endUser)
            queryObject.end_user = queryParameters.endUser;
          break;
        case "supportPartner":
          if (queryParameters?.supportPartner)
            queryObject.support_partner = queryParameters.supportPartner;
          break;
        case "bundleReference":
          if (queryParameters?.bundleReference)
            queryObject.bundle_reference = queryParameters.bundleReference;
          break;
        case "swapStatus":
          if (queryParameters?.swapStatus)
            queryObject.swap_status = queryParameters.swapStatus;
          break;

        case "creationDate":
          if (
            queryParameters?.creationDateStart &&
            queryParameters?.creationDateEnd
          ) {
            queryObject.creation_date_from = queryParameters.creationDateStart;
            queryObject.creation_date_to = queryParameters.creationDateEnd;
          }
          break;
        default:
          break;
      }
    });

    dispatch(getMaterials(queryObject));
  };

  const handleExecuteBulkSearch = () => {
    let queryObject = {};
    const currentTab = tabs[selectedTab];

    currentTab.filters.forEach((filter) => {
      switch (filter.type) {
        case "serialNumbers":
          if (queryParameters?.serialNumbers)
            queryObject.serial_number = queryParameters.serialNumbers;
          break;
        case "businessPartners":
          if (queryParameters?.businessPartners)
            queryObject.business_partner = queryParameters.businessPartners;
          break;
        case "soldTos":
          if (queryParameters?.soldTos)
            queryObject.sold_to = queryParameters.soldTos;
          break;
        case "order":
          if (queryParameters?.order) queryObject.order = queryParameters.order;
          break;
        case "endUser":
          if (queryParameters?.endUser)
            queryObject.end_user = queryParameters.endUser;
          break;
        case "supportPartner":
          if (queryParameters?.supportPartner)
            queryObject.support_partner = queryParameters.supportPartner;
          break;
        case "bundleReference":
          if (queryParameters?.bundleReference)
            queryObject.bundle_reference = queryParameters.bundleReference;
          break;
        case "creationDate":
          if (
            queryParameters?.creationDateStart &&
            queryParameters?.creationDateEnd
          ) {
            queryObject.creation_date_from = queryParameters.creationDateStart;
            queryObject.creation_date_to = queryParameters.creationDateEnd;
          }
          break;
        default:
          break;
      }
    });

    const saml_token = getLocalStorageSAMLToken();
    const infoToken = extractUserInfoFromToken(saml_token);
    queryObject.email = infoToken.email;
    queryObject.request_user = infoToken.username;
    dispatch(getMaterialsBulk(queryObject));
  };

  const handleSelectMaterial = (serialNumber, orderId, orderLine) => {
    const materialParams = {
      serialNumber: serialNumber,
      orderId: orderId,
      orderLine: orderLine,
    };
    dispatch(getMaterialDetails(materialParams));
    navigate("/materials/details/" + serialNumber);
  };

  const handleDeleteSerialNumber = (chipToDelete) => () => {
    dispatch(removeParameter({ materials: { serialNumbers: [chipToDelete] } }));
  };

  const handleClearSerialNumbers = () => {
    dispatch(clearParameters({ materials: "serialNumbers" }));
  };

  const handleDeleteBusinessPartner = (chipToDelete) => () => {
    dispatch(
      removeParameter({ materials: { businessPartners: [chipToDelete] } }),
    );
  };

  const handleClearBusinessPartners = () => {
    dispatch(clearParameters({ materials: "businessPartners" }));
  };

  const handleDeleteSoldTo = (chipToDelete) => () => {
    dispatch(removeParameter({ materials: { soldTos: [chipToDelete] } }));
  };

  const handleClearSoldTo = () => {
    dispatch(clearParameters({ materials: "soldTos" }));
  };

  const handleDeleteOrder = (chipToDelete) => () => {
    dispatch(removeParameter({ materials: { order: [chipToDelete] } }));
  };

  const handleClearOrder = () => {
    dispatch(clearParameters({ materials: "order" }));
  };

  const handleDeleteEndUser = (chipToDelete) => () => {
    dispatch(removeParameter({ materials: { endUser: [chipToDelete] } }));
  };

  const handleClearEndUser = () => {
    dispatch(clearParameters({ materials: "endUser" }));
  };

  const handleDeleteBundleReference = (chipToDelete) => () => {
    dispatch(
      removeParameter({ materials: { bundleReference: [chipToDelete] } }),
    );
  };

  const handleClearBundleReference = () => {
    dispatch(clearParameters({ materials: "bundleReference" }));
  };

  const handleDeleteSupportPartner = (chipToDelete) => () => {
    dispatch(
      removeParameter({ materials: { supportPartner: [chipToDelete] } }),
    );
  };

  const handleClearSupportPartner = () => {
    dispatch(clearParameters({ materials: "supportPartner" }));
  };

  const handleDeleteSwapStatus = (chipToDelete) => () => {
    dispatch(removeParameter({ materials: { swapStatus: [chipToDelete] } }));
  };

  const handleClearSwapStatus = () => {
    dispatch(clearParameters({ materials: "swapStatus" }));
  };

  const handleExport = (materials, format = "csv") => {
    const fields = [
      { name: "serial_number" },
      { name: "status" },
      { name: "inactive_reason" },
      { name: "order_id" },
      { name: "part_number" },
      { name: "business_partner", value: "business_partner_company_id" },
      { name: "support_partner", value: "support_partner_company_id" },
      { name: "end_user", value: "end_user_company_id" },
      { name: "children" },
    ];
    if (format === "csv") {
      downloadCSV(fields, "material_export", ",", materials);
    } else {
      downloadJSON(fields, "material_export", materials);
    }
  };

  const handleExportContractsToCSV = (materials) => {
    dispatch(exportMaterialContractsToCSV(materials));
  };

  const materials = useSelector((state) => {
    if (state.filteredMaterials) {
      return state.filteredMaterials.map((serialNumber) => ({
        modification_date: serialNumber.modification_date,
        part_number: serialNumber.part_number,
        inactive_reason: serialNumber.inactive_reason,
        status: serialNumber.status,
        creation_note: serialNumber.creation_note,
        comments: serialNumber.comments,
        serial_number: serialNumber.serial_number,
        sn_type: serialNumber.sn_type,
        description: serialNumber.description,
        source_time: serialNumber.source_time,
        type: serialNumber.type,
        creation_date: serialNumber.creation_date,
        sold_to: serialNumber?.sold_to?.applicant_id,
        order_line: serialNumber?.order?.order_line,
        order_id: serialNumber?.order?.order_id,
        purchase_order: serialNumber?.order?.purchase_order,
        end_user_source: serialNumber?.end_user?.source,
        end_user_company_id: serialNumber?.end_user?.company_id,
        business_partner_source: serialNumber?.business_partner?.source,
        business_partner_company_id: serialNumber?.business_partner?.company_id,
        support_partner_source: serialNumber?.support_partner?.source,
        support_partner_company_id: serialNumber?.support_partner?.company_id,
        children: serialNumber?.children,
        parent: serialNumber?.parent,
      }));
    }
    return [];
  });
  const statusValues = ["Replaced - Waiting HWS In"];

  const [openBulkConfirmation, setOpenBulkConfirmation] = React.useState(false);
  const [openBulkMessage, setOpenBulkMessage] = React.useState(false);

  const handleKeydown = (event) => {
    if (event.key === "Enter") {
      event.stopPropagation();
      event.preventDefault();
      setTimeout(() => {
        handleExecuteSearch();
      }, 0);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeydown);
    return () => {
      document.removeEventListener("keydown", handleKeydown);
    };
  }, []);

  return (
    <React.Fragment>
      <Dialog open={openBulkMessage} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            Your query has been sent. You can follow the status in the Bulk
            Report page or wait for the Rainbow message that will announce the
            end of the export.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setOpenBulkMessage(false)}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={openBulkConfirmation} maxWidth="xs" fullWidth>
        <DialogContent>
          <Typography variant="body2">
            This query could take a few minutes, are you sure you want to
            continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              handleExecuteBulkSearch();
              setOpenBulkConfirmation(false);
              setOpenBulkMessage(true);
            }}
          >
            Yes
          </Button>

          <Button variant="text" onClick={() => setOpenBulkConfirmation(false)}>
            No
          </Button>
        </DialogActions>
      </Dialog>

      <Tabs
        value={selectedTab}
        onChange={handleTabChange}
        aria-label="filter tabs"
      >
        {tabs.map((tab, index) => (
          <Tab label={tab.label} key={index} />
        ))}
      </Tabs>

      {tabs.map((tab, index) => (
        <div role="tabpanel" hidden={selectedTab !== index} key={index}>
          {selectedTab === index && (
            <Box sx={{ p: 3 }}>
              {tab.filters.map((filter, i) => (
                <React.Fragment key={i}>
                  {filter.type === "serialNumbers" && (
                    <MultiValueInput
                      setCurrentParam={setCurrentSerialNumber}
                      setQueryParams={setParameters}
                      currentParam={currentSerialNumber}
                      queryParams={queryParameters?.serialNumbers}
                      handleDelete={handleDeleteSerialNumber}
                      handleClear={handleClearSerialNumbers}
                      label={filter.label}
                      family="materials"
                      type="serialNumbers"
                    />
                  )}
                  {filter.type === "businessPartners" && (
                    <MultiValueInput
                      setCurrentParam={setCurrentBusinessPartner}
                      setQueryParams={setParameters}
                      currentParam={currentBusinessPartner}
                      queryParams={queryParameters?.businessPartners}
                      handleDelete={handleDeleteBusinessPartner}
                      handleClear={handleClearBusinessPartners}
                      label={filter.label}
                      family="materials"
                      type="businessPartners"
                    />
                  )}
                  {filter.type === "soldTos" && (
                    <MultiValueInput
                      setCurrentParam={setCurrentSoldTo}
                      setQueryParams={setParameters}
                      currentParam={currentSoldTo}
                      queryParams={queryParameters?.soldTos}
                      handleDelete={handleDeleteSoldTo}
                      handleClear={handleClearSoldTo}
                      label={filter.label}
                      family="materials"
                      type="soldTos"
                    />
                  )}
                  {filter.type === "order" && (
                    <MultiValueInput
                      setCurrentParam={setCurrentOrder}
                      setQueryParams={setParameters}
                      currentParam={currentOrder}
                      queryParams={queryParameters?.order}
                      handleDelete={handleDeleteOrder}
                      handleClear={handleClearOrder}
                      label={filter.label}
                      family="materials"
                      type="order"
                    />
                  )}
                  {filter.type === "endUser" && (
                    <MultiValueInput
                      setCurrentParam={setCurrentEndUser}
                      setQueryParams={setParameters}
                      currentParam={currentEndUser}
                      queryParams={queryParameters?.endUser}
                      handleDelete={handleDeleteEndUser}
                      handleClear={handleClearEndUser}
                      label={filter.label}
                      family="materials"
                      type="endUser"
                    />
                  )}
                  {filter.type === "supportPartner" && (
                    <MultiValueInput
                      setCurrentParam={setCurrentSupportPartner}
                      setQueryParams={setParameters}
                      currentParam={currentSupportPartner}
                      queryParams={queryParameters?.supportPartner}
                      handleDelete={handleDeleteSupportPartner}
                      handleClear={handleClearSupportPartner}
                      label={filter.label}
                      family="materials"
                      type="supportPartner"
                    />
                  )}
                  {filter.type === "bundleReference" && (
                    <MultiValueInputWithWarning
                      setCurrentParam={setCurrentBundleReference}
                      setQueryParams={setParameters}
                      currentParam={currentBundleReference}
                      queryParams={queryParameters?.bundleReference}
                      handleDelete={handleDeleteBundleReference}
                      handleClear={handleClearBundleReference}
                      warningText="Warning: Query can be large, results may be incomplete"
                      label={filter.label}
                      family="materials"
                      type="bundleReference"
                    />
                  )}
                  {filter.type === "swapStatus" && (
                    <MultiSelector
                      setQueryParams={setParameters}
                      queryParams={queryParameters?.swapStatus}
                      handleDelete={handleDeleteSwapStatus}
                      handleClear={handleClearSwapStatus}
                      label={filter.label}
                      family="materials"
                      type="swapStatus"
                      options={statusValues}
                    />
                  )}
                  {filter.type === "creationDate" && (
                    <Grid>
                      <SearchDateInput
                        title={filter.label}
                        setFilterDate={setDateParameters}
                        family="materials"
                        type="creationDateStart"
                        type2="creationDateEnd"
                      />
                    </Grid>
                  )}
                </React.Fragment>
              ))}
            </Box>
          )}
        </div>
      ))}

      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
        <Button
          variant="contained"
          startIcon={<MaterialSymbol name="search" size="button" />}
          onClick={handleExecuteSearch}
        >
          Search
        </Button>
        {selectedTab === 0 && (
          <Button
            variant="outlined"
            startIcon={<MaterialSymbol name="cloud_download" size="button" />}
            onClick={() => setOpenBulkConfirmation(true)}
          >
            Bulk Export
          </Button>
        )}
      </Stack>

      {materials.length > 0 && (
        <DataTable
          title="Materials"
          subtitle={
            materials.length === 100
              ? `${materials.length} results, use bulk export for more`
              : `${materials.length} results`
          }
          columns={MATERIAL_COLUMNS}
          rows={materials}
          getRowId={(row, i) => `${row?.serial_number}-${i}`}
          onRowClick={(row) =>
            handleSelectMaterial(
              row?.serial_number,
              row?.order_id,
              row?.order_line,
            )
          }
          isExpandable={(row) => Boolean(row?.children || row?.parent)}
          renderExpanded={(row) => (
            <Stack spacing={2}>
              {row?.children && <MaterialChildren material={row} />}
              {row?.parent && <MaterialParents material={row} />}
            </Stack>
          )}
          defaultRowsPerPage={25}
          size="small"
          actions={(rows) => (
            <>
              <ExportMenu
                disabled={!rows.length}
                onExport={(format) => handleExport(rows, format)}
              />
              <Button
                variant="outlined"
                startIcon={<MaterialSymbol name="description" size="button" />}
                disabled={!rows.length}
                onClick={() => handleExportContractsToCSV(rows)}
              >
                Export contracts
              </Button>
            </>
          )}
          sx={{ mt: 3 }}
        />
      )}
    </React.Fragment>
  );
}
